import { format } from 'date-fns'
import { motion } from 'framer-motion'
import {
  Calendar,
  Clock,
  Trash2,
  Users,
  Scale,
  MapPin,
  Car,
} from 'lucide-react'
import { CartItem, getActivityEmoji, requiresWeight } from '@/types/cart.types'
import { useCart } from '@/contexts/CartContext'
import { useTranslation } from 'react-i18next'

interface CartItemCardProps {
  item: CartItem
}

export default function CartItemCard({ item }: CartItemCardProps) {
  const { removeItem } = useCart()
  const { t } = useTranslation('cart')

  const formattedDate = format(new Date(item.selectedDate), 'EEE, MMM d, yyyy')
  const emoji = getActivityEmoji(item.activityType)

  return (
    <div className="group relative overflow-hidden rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-all duration-300">
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-garage-accent/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Content */}
      <div className="relative p-4">
        {/* Header with activity info and remove button */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Activity image/emoji */}
            {item.activityImage ? (
              <div className="relative flex-shrink-0">
                <img
                  src={item.activityImage}
                  alt={item.activityTitle}
                  className="w-14 h-14 rounded-xl object-cover"
                />
                <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-white/10" />
              </div>
            ) : (
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center flex-shrink-0 border border-white/10">
                <span className="text-2xl">{emoji}</span>
              </div>
            )}

            <div className="min-w-0 flex-1">
              <h4 className="font-bold text-white truncate leading-tight">
                {item.activityTitle}
              </h4>
              {item.seasonName && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-garage-accent/20 text-garage-accent text-xs font-medium">
                  <span className="w-1 h-1 rounded-full bg-garage-accent" />
                  {item.seasonName}
                </span>
              )}
            </div>
          </div>

          {/* Remove button */}
          <motion.button
            onClick={() => removeItem(item.id)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-2 text-white/30 hover:text-red-400 hover:bg-red-500/10
                       rounded-lg transition-all flex-shrink-0"
            aria-label={t('removeItem')}
          >
            <Trash2 className="h-4 w-4" />
          </motion.button>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2 text-white/60 text-sm bg-white/5 rounded-lg px-3 py-2">
            <Calendar className="h-4 w-4 text-garage-accent flex-shrink-0" />
            <span className="truncate">{formattedDate}</span>
          </div>

          <div className="flex items-center gap-2 text-white/60 text-sm bg-white/5 rounded-lg px-3 py-2">
            <Users className="h-4 w-4 text-garage-accent flex-shrink-0" />
            <span>{t('ckPerson', { count: item.partySize })}</span>
          </div>

          {item.flightTime && (
            <div className="flex items-center gap-2 text-white/60 text-sm bg-white/5 rounded-lg px-3 py-2 col-span-2">
              <Clock className="h-4 w-4 text-garage-accent flex-shrink-0" />
              <span>
                {item.flightTime === 'sunrise'
                  ? t('sunriseFlight')
                  : t('sunsetFlight')}
              </span>
            </div>
          )}

          {item.transportOption?.enabled && (
            <div className="flex items-center gap-2 text-white/60 text-sm bg-garage-accent/10 rounded-lg px-3 py-2 col-span-2">
              <Car className="h-4 w-4 text-garage-accent flex-shrink-0" />
              <span className="flex-1">{t('transportIncluded')}</span>
              <span className="text-garage-accent font-medium">
                +${item.transportOption.price}
              </span>
            </div>
          )}
        </div>

        {/* Guest details for flight activities */}
        {requiresWeight(item.activityType) && item.guestDetails.length > 0 && (
          <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/5">
            <div className="flex items-center gap-2 text-white/50 text-xs font-medium mb-2">
              <Scale className="h-3 w-3" />
              <span>{t('guestDetails')}</span>
            </div>
            <div className="space-y-1">
              {item.guestDetails.map((guest, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-white/70">
                    {idx + 1}. {guest.name || t('guest', { n: idx + 1 })}
                  </span>
                  {guest.weight && (
                    <span className="text-white/40 text-xs">
                      {guest.weight} kg
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes if present */}
        {item.notes && (
          <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/5">
            <div className="flex items-start gap-2">
              <MapPin className="h-3 w-3 text-white/40 mt-0.5 flex-shrink-0" />
              <p className="text-white/50 text-xs italic">{item.notes}</p>
            </div>
          </div>
        )}

        {/* Price section */}
        <div className="pt-4 border-t border-white/10 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-white/50 text-sm">
              <span className="text-white/70">${item.pricePerPerson}</span>
              <span className="mx-1">×</span>
              <span>{item.partySize}</span>
            </div>
            <span className="text-white/70 font-medium">
              ${item.pricePerPerson * item.partySize}
            </span>
          </div>
          {item.transportOption?.enabled && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/50">{t('transport')}</span>
              <span className="text-white/70 font-medium">
                +${item.transportOption.price}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <span className="text-white/70 text-sm font-medium">
              {t('total')}
            </span>
            <span className="text-2xl font-bold bg-gradient-to-r from-garage-accent to-orange-400 bg-clip-text text-transparent">
              ${item.subtotal}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
